"use client";

// WO-GF-001-P1B-S17 · F127 / F128 — THE TWO "INFO" MODALS.
//
// Founder screenshots, 2026-07-23. Both correct a destination I shipped:
//
//   • "Membership Info/Signup" — S13/RW3-11 wired it to `/general-information#snowclub`.
//     LIVE OPENS A SNOWCLUB MODAL.
//   • "Agent & Partner"        — S16/F119 wired it to `/general-information#become-an-agent`.
//     LIVE OPENS A BUSINESS-SIGNUP MODAL.
//
// In both cases the anchor genuinely exists on the ported general-information page, which is why the
// inference looked safe. It was still an inference: an anchor existing is not evidence that a control
// navigates to it. The pattern across both is that hamburger/menu "Info" items open dialogs.
//
// ⚠ S13 RECORDED THE OPPOSITE, AND THAT OBSERVATION IS NOW SUPERSEDED. RW3-11 logged: "live agrees it is
// dead — href='javascript:void(0);', click ⇒ no navigation AND NO MODAL." The href reading was right (it
// is a JS control, not a link) but the "no modal" half was wrong. Most likely the same class-hashed
// trigger flakiness that produced F-W6-parity's flip-flop, where a click on live silently failed to
// register. The founder's screenshots are the authoritative observation; a failed click is not evidence
// of absence.
//
// CONTENT is transcribed from the screenshots. Destinations are NOT invented: the one link whose target
// is not observable ("Create A New Business Account") renders inert and is recorded, exactly as the F119
// menu handles Saved Items.

import {
  MEMBERSHIP_BENEFITS,
  MEMBERSHIP_SIGNOFF,
} from "@repo/ui/lib/signup-funnel";

import { ModalShell } from "./ModalShell";

const TEAL = "#4aa3a8";
const ORANGE = "#f15a22";

/** F128 — live's SNOWCLUB membership modal. */
export function MembershipModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell title="" onClose={onClose} data-modal="membership">
      <div data-membership-modal style={{ textAlign: "center", maxHeight: "76vh", overflowY: "auto" }}>
        {/* ── S35 · F222 — "MEMBER INFO MISSING ITEMS" ────────────────────────────────────────────
            The founder walked this dialog on 2026-07-27 and he is right. Measured live vs build at
            1440×900 (read-only, harness UA, no forms, no logins): the TEXT inventory already matched
            exactly — same 4 headline bullets, same 10 benefits, same closing block — which is why a
            text-only comparison found nothing and why this survived. What live carries and the build
            did not is FOUR ARTWORK ITEMS, and their absence is most of the dialog:
                live card 800×1211   ·   build card 840×595
            The four are live's own assets, pulled into the mirror at their live paths — nothing here
            is redrawn or approximated. Each SVG's intrinsic viewBox equals the size live renders it
            at, which is how the four were matched to the four measured boxes:
                membersIcon      97×113   viewBox 0 0 97 113
                memberLogoImg   281×234   viewBox 0 0 281 234
                membersiconList 734×48    viewBox 0 0 734 48
                freeSignUp      109×72    viewBox 0 0 109 72   ← the "sign-up" the founder named
            ORDER is live's own DOM order (logo + icon in the header block, the icon strip between the
            bullets and "some member benefits", the free-signup mark in the Join-the-Club block).
            PLACEMENT WAS A RECONSTRUCTION AND IS NOT ANY MORE (attempt 8). The first cut flagged its
            own offsets as guessed, because live's dialog is click-gated and no capture of it exists
            in the repo. So it was measured directly instead — driven open read-only and every
            landmark positioned against the dialog's own card. Live (800×1211):
                membersIcon      y=22   centred, ALONE above the heading
                SNOWCLUB         y=135
                memberLogoImg    y=266  centred, BELOW the heading and above the bullets
                membersiconList  y=692  full width
                SOME MEMBER…     y=791
                freeSignUp       y=1089 x=616 → right edge 725, i.e. FLUSH RIGHT
            Two of those contradicted the reconstruction and are corrected here: the two header images
            were stacked side-by-side (live splits them around the heading), and freeSignUp sat LEFT
            of the wording (live puts it right). The vertical ORDER is now gated — see
            chrome/membership-dialog-carries-live-artwork. Exact pixel offsets still are not: there is
            no pixel baseline for a dialog live never renders without a click, and inventing one would
            be asserting this session's layout as live's. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/_mirror/_next/static/media/membersIcon.36d33f73.svg"
          alt=""
          width={97}
          height={113}
          style={{ display: "block", margin: "6px auto 2px" }}
        />
        <h2 style={{ fontSize: 34, letterSpacing: 2, margin: "4px 0 2px", color: "#555" }}>SNOWCLUB</h2>
        <p style={{ margin: 0, color: "#777", fontSize: 14 }}>
          The Ultimate Multi-Mountain
          <br />
          Online Community &amp; Discount Network
        </p>

        {/* S35 · F222 (attempt 8) — PLACEMENT IS NOW MEASURED, NOT RECONSTRUCTED. The first cut put
            both header images side by side above the heading and flagged that as a guess. Live's own
            dialog, measured relative to its card (800×1211): membersIcon centred ALONE at y=22, the
            SNOWCLUB heading at y=135, and memberLogoImg centred BELOW it at y=266 — i.e. the logo
            sits between the heading and the four bullets, which is where it now is. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/_mirror/_next/static/media/memberLogoImg.7ab26122.svg"
          alt="SnowClub"
          width={281}
          height={234}
          style={{ display: "block", margin: "10px auto 0" }}
        />

        <ul
          style={{
            listStyle: "none",
            margin: "18px auto",
            padding: "16px 20px",
            background: "#f5f7f8",
            borderRadius: 10,
            maxWidth: 460,
            color: "#555",
            fontWeight: 600,
            lineHeight: 1.9,
          }}
        >
          <li>Huge Discounts Online &amp; Offline</li>
          <li>Exclusive Access To Online Products</li>
          <li>Buy &amp; Sell New Or Used Gear Online</li>
          <li>Exclusive Access To Promos &amp; Events</li>
        </ul>

        {/* F222 — live's icon strip sits between the bullets and the benefits heading (measured
            734×48 at y=699, the untitled full-width row in live's own DOM order). */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/_mirror/_next/static/media/membersiconList.93ecafec.svg"
          alt=""
          width={734}
          height={48}
          style={{ display: "block", margin: "14px auto 0", maxWidth: "100%", height: "auto" }}
        />
        <h3 style={{ fontWeight: 400, color: "#555", margin: "18px 0 12px", fontSize: 22 }}>
          some member benefits
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "6px 24px",
            textAlign: "left",
            maxWidth: 620,
            margin: "0 auto",
            color: "#555",
          }}
        >
          {/* Card 86bb671n4 — same strings as before, now read from `@repo/ui/lib/signup-funnel` so the
              accounts page cannot render a different wording for the same popup. See MEMBERSHIP_BENEFITS
              there: this surface's text is the authority and did not change. */}
          {MEMBERSHIP_BENEFITS.map((b) => (
            <span key={b} style={{ display: "flex", gap: 8 }}>
              <span style={{ color: TEAL }}>»</span>
              {b}
            </span>
          ))}
        </div>

        <div
          style={{
            margin: "22px auto 4px",
            padding: "18px",
            background: "#f2f7f8",
            borderRadius: 8,
            maxWidth: 620,
          }}
        >
          {/* F222 — the free-signup mark lives in this block on live (measured 109×72), and it sits on
              the RIGHT: on live's 800-wide card it is at x=616, so its right edge lands at 725 — flush
              right, not left of the wording. The first cut had it on the left; corrected against the
              measurement rather than left as a guess. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14 }}>
            <div>
              <strong style={{ fontSize: 26, color: TEAL, letterSpacing: 1 }}>{MEMBERSHIP_SIGNOFF.lead}</strong>
              <div style={{ fontSize: 22, color: ORANGE, fontWeight: 700 }}>{MEMBERSHIP_SIGNOFF.tail}</div>
            </div>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/_mirror/_next/static/media/freeSignUp.64bc3ac0.svg" alt="Free sign up" width={109} height={72} />
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

/** F127 — live's business-signup modal, behind "Agent & Partner". */
export function AgentPartnerModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell title="" onClose={onClose} data-modal="agent-partner">
      <div data-agent-modal style={{ textAlign: "center", maxHeight: "76vh", overflowY: "auto" }}>
        <h2 style={{ fontSize: 26, color: "#666", letterSpacing: 3, margin: "4px 0 0", fontWeight: 400 }}>
          Your Business On
        </h2>
        <div style={{ fontSize: 32, fontWeight: 700, color: "#444", marginBottom: 16 }}>
          The Frontline!
        </div>

        <div
          style={{
            background: "#f5f7f8",
            borderRadius: 10,
            padding: "18px 22px",
            maxWidth: 560,
            margin: "0 auto",
            color: "#666",
            lineHeight: 1.7,
            textAlign: "justify",
          }}
        >
          Are you a <strong>transport</strong> company, <strong>rental, activity,</strong> or{" "}
          <strong>retail store, hotel,</strong> or <strong>restaurant</strong>! You can promote and sell
          your products and services direct to skiers and boarders online! Create an account today !
          <div style={{ textAlign: "center", marginTop: 14 }}>
            {/* The target of this link is NOT observable from a screenshot. Rendered inert and recorded
                rather than pointed at a plausible guess (the vendors app is a candidate, not evidence). */}
            <span
              data-agent-create-account
              data-agent-inert
              style={{ color: TEAL, textDecoration: "underline", fontSize: 18, cursor: "default" }}
            >
              Create A New Business Account
            </span>
          </div>
        </div>

        <a
          data-agent-contact
          href="/contact-us"
          style={{
            display: "inline-block",
            marginTop: 22,
            padding: "12px 42px",
            border: `1px solid ${TEAL}`,
            borderRadius: 8,
            color: TEAL,
            fontSize: 20,
            textDecoration: "none",
          }}
        >
          Contact Us
        </a>
      </div>
    </ModalShell>
  );
}
